import React, { ChangeEvent, useContext, useEffect, useState } from "react";
import "./ShopifyShopModal.scss";
import OwButton from "../../../../shared/OwButton/OwButton";
import IErrorDTO from "../../../../../models/DTO/IErrorDTO";
import OwTitle from "../../../../shared/OwTitle/OwTitle";
import IShopify from "../../../../../models/IShopify";
import OwLabel from "../../../../shared/OwLabel/OwLabel";
import { OwFnInput } from "../../../../shared/OwFnInput/OwFnInput";
import OwSelectOption from "../../../../shared/OwSelectOption/OwSelectOption";
import ICatalog from "../../../../../models/ICatalog";
import IPrice_Group from "../../../../../models/IPrice_Group";
import IShopifyBeginOAuthDTO from "../../../../../models/DTO/IShopifyBeginOAuthDTO";
import UserContext from "../../../../../contexts/UserContext";
import SupplierContext from "../../../../../contexts/SupplierContext";
import shopifyLogo from "../../../../../assets/images/shopify/shopify-logo.svg";
import shopifyLightLogo from "../../../../../assets/images/shopify/shopify-logo-light.svg";
import IShopifyUpdateShopDTO from "../../../../../models/DTO/IShopifyUpdateShopDTO";
import EndpointsEnum from "../../../../../models/EndpointsEnum";
import IShopifyOnUpdateShopDTO from "../../../../../models/DTO/IShopifyOnUpdateShopDTO";
import INotification from "../../../../../models/INotification";
import NotificationPositionEnum from "../../../../../models/NotificationPositionEnum";
import NotificationLevelEnum from "../../../../../models/NotificationLevelEnum";
import IAppConnectParams from "../../../../../models/IAppConnectParams";
import IShopifyFinishPartialOAuthDTO from "../../../../../models/DTO/IShopifyFinishPartialOAuthDTO";
import { Socket } from "socket.io-client";

type Props = {
    closeModalFn: () => void;
    socket: Socket;
    notifyFn: (data: IErrorDTO | null, _notification?: any, isFormatted?: boolean) => void;
    shopifyShop?: IShopify;
    catalogs: ICatalog[];
    create: (payload: IShopifyBeginOAuthDTO) => void;
    update: (payload: IShopifyUpdateShopDTO) => void;
    finishPartialOAuth?: (payload: IShopifyFinishPartialOAuthDTO) => void;
    isMainViewLight?: boolean;
};

export const ShopifyShopModal: React.FC<Props> = (props) => {
    const userModel = useContext(UserContext);
    const supplierModel = useContext(SupplierContext);

    const getLogo = () => {
        let logo = shopifyLogo;

        if (props.isMainViewLight) {
            logo = shopifyLightLogo;
        }

        return logo;
    };

    const [shopifyShop, setShopifyShop] = useState<Partial<IShopify>>(
        props.shopifyShop || { shopify_shop: "", skip_inventory_updates: false, skip_pricing_updates: false }
    );
    const [catalogId, setCatalogId] = useState<number | undefined>(props.shopifyShop?.catalog_id);
    const [priceGroupId, setPriceGroupId] = useState<number | undefined>(props.shopifyShop?.price_group_id);
    const [priceGroups, setPriceGroups] = useState<IPrice_Group[]>([]);
    const [logo, setLogo] = useState(getLogo());
    const [disableSave, setDisableSave] = useState(false);

    useEffect(() => {
        setupPriceGroups();
    }, [catalogId]);

    const setupPriceGroups = () => {
        console.log("setupPriceGroups", shopifyShop);

        if (shopifyShop && shopifyShop.catalog_id) {
            const selectedCatalog = props.catalogs.find((catalog) => catalog.id.toString() === (shopifyShop.catalog_id || "").toString());

            if (selectedCatalog) {
                setPriceGroups(selectedCatalog.Price_Groups || []);
            }
        }
    };

    useEffect(() => {
        setupPriceGroups();

        props.socket.on(EndpointsEnum.ShopifyOnUpdateShop, onUpdateShop);

        return () => {
            props.socket.removeListener(EndpointsEnum.ShopifyOnUpdateShop, onUpdateShop);
        };
    }, []);

    const closeModal = () => {
        props.closeModalFn();
    };

    const update = () => {
        if (catalogId && priceGroupId) {
            const selectedCatalog = props.catalogs.find((catalog) => catalog.id.toString() === catalogId.toString());

            if (props.shopifyShop && selectedCatalog) {
                const selectedPriceGroup = (selectedCatalog.Price_Groups || []).find(
                    (priceGroup) => priceGroup.id.toString() === shopifyShop.price_group_id?.toString()
                );

                const catalogGuid = selectedCatalog?.guid || "";
                const priceGroupGuid = selectedPriceGroup?.guid || "";

                const payload: IShopifyUpdateShopDTO = {
                    userGuid: userModel.guid,
                    userToken: userModel.token,
                    supplierGuid: supplierModel.guid,
                    priceGroupGuid,
                    catalogGuid,
                    shopGuid: props.shopifyShop.guid,
                };

                props.update(payload);
            }
        }
    };

    const onUpdateShop = (data: IShopifyOnUpdateShopDTO) => {
        const notification: INotification = {
            message: "successfully_updated_shop",
            position: NotificationPositionEnum.bc,
            level: NotificationLevelEnum.success,
        };

        props.notifyFn(null, notification);

        props.closeModalFn();
    };

    const create = () => {
        const selectedCatalog = props.catalogs.find((catalog) => catalog.id.toString() === shopifyShop.catalog_id?.toString());

        if (selectedCatalog && shopifyShop.shopify_shop) {
            const selectedPriceGroup = (selectedCatalog.Price_Groups || []).find(
                (priceGroup) => priceGroup.id.toString() === shopifyShop.price_group_id?.toString()
            );

            const catalogGuid = selectedCatalog?.guid || "";
            const priceGroupGuid = selectedPriceGroup?.guid || "";

            if (shopifyShop.shopify_oauth_nonce) {
                const payload: IShopifyFinishPartialOAuthDTO = {
                    userGuid: userModel.guid,
                    userToken: userModel.token,
                    supplierGuid: supplierModel.guid,
                    shopify_shop: shopifyShop.shopify_shop || "",
                    skip_pricing_updates: shopifyShop.skip_pricing_updates || false,
                    skip_inventory_updates: shopifyShop.skip_inventory_updates || false,
                    priceGroupGuid,
                    catalogGuid,
                    nonce: shopifyShop.shopify_oauth_nonce,
                };

                if (props.finishPartialOAuth) {
                    props.finishPartialOAuth(payload);
                }
            } else {
                const payload: IShopifyBeginOAuthDTO = {
                    userGuid: userModel.guid,
                    userToken: userModel.token,
                    supplierGuid: supplierModel.guid,
                    shopify_shop: shopifyShop.shopify_shop || "",
                    skip_pricing_updates: shopifyShop.skip_pricing_updates || false,
                    skip_inventory_updates: shopifyShop.skip_inventory_updates || false,
                    priceGroupGuid,
                    catalogGuid,
                };

                props.create(payload);
            }
        }
    };

    const existingShopName = (name: string): boolean => {
        let exists = false;

        const shops = supplierModel.Shopifies || [];

        for (const shop of shops) {
            if (shop.shopify_shop === name) {
                exists = true;
            }
        }

        return exists;
    };

    const handleChange = (e: ChangeEvent<any>) => {
        const name = e.target.name;
        const value = e.target.type === "checked" ? e.target.checked : e.target.value;

        const _shop = {
            ...shopifyShop,
        };

        // @ts-expect-error
        _shop[name] = value;

        setShopifyShop(_shop);

        if (name === "catalog_id") {
            setCatalogId(Number(value));
        }

        if (name === "price_group_id") {
            setPriceGroupId(Number(value));
        }

        if (name === "shopify_shop") {
            if (existingShopName(value)) {
                setDisableSave(true);
            } else {
                setDisableSave(false);
            }
        }
    };

    return (
        <div className={"ShopifyShopModal p-2"}>
            <div className={"w-100"}>
                <div className={""}>
                    <div className={"d-flex justify-content-between"}>
                        <OwTitle title={props.shopifyShop?.guid ? "update_shopify_shop" : "add_shopify_shop"} tag={"h3"} />
                        <div>
                            <OwButton className={"btn-danger"} centerIconClass={"fas fa-times"} fn={closeModal} />
                        </div>
                    </div>
                    <div>
                        <img src={logo} height={32} />
                    </div>
                </div>
            </div>

            <div>
                <div>
                    <OwLabel title={"name"} />
                    <OwFnInput fn={handleChange} name={"shopify_shop"} value={shopifyShop.shopify_shop} disabled={!!props.shopifyShop} />
                    {disableSave ? (
                        <div>
                            <OwLabel title={"shop_with_existing_name"} className={"text-danger"} />
                        </div>
                    ) : null}
                </div>

                <div className={"mt-3"}>
                    <OwLabel title={"catalog"} />
                    <OwSelectOption
                        name={"catalog_id"}
                        options={props.catalogs}
                        fn={handleChange}
                        selectedOption={shopifyShop}
                        selectedOptionProp={"catalog_id"}
                        valueProp={"id"}
                        titleProp={"name"}
                    />
                </div>

                <div className={"mt-3"}>
                    <OwLabel title={"price_group"} />
                    <OwSelectOption
                        name={"price_group_id"}
                        options={priceGroups}
                        fn={handleChange}
                        selectedOption={shopifyShop}
                        selectedOptionProp={"price_group_id"}
                        valueProp={"id"}
                        titleProp={"value"}
                    />
                </div>
            </div>
            <div className={"mt-3"}>
                <OwButton
                    title={props.shopifyShop?.guid ? "update" : "create"}
                    fn={props.shopifyShop?.guid ? update : create}
                    disabled={disableSave}
                />
                <OwButton className={"btn-danger ms-3"} title={"cancel"} fn={closeModal} />
            </div>
        </div>
    );
};
